import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/components/ui/Price';
import { useCatalogText } from '@/lib/localizeCatalog';
import { useModalBackClose } from '@/features/waiterTicket/useModalBackClose';
import {
  addableGroups,
  clampSelection,
  firstQty,
  maxPerPlate,
  optionUnitCents,
  type AddonSelection,
} from './optionAddons';
import type { ConsumerMode } from './resolveModePrice';
import type { MenuItem, OptionGroup } from '@/types';

export interface AddOptionsPayload {
  plates: number;
  options: { option_value_uuid: string; qty: number }[];
}

export interface AddOptionsSheetProps {
  product: MenuItem;
  /** Le nom de la ligne envoyée, tel que le client l'a commandée. */
  dishName: string;
  plateCount: number;
  carried: Map<string, number>;
  isBuffetDineIn: boolean;
  mode: ConsumerMode;
  onCancel: () => void;
  /** Rejette avec le `{ status, data: { code } }` de RTK Query : la fenêtre reste
   *  ouverte et dit pourquoi. */
  onSubmit: (payload: AddOptionsPayload) => Promise<void>;
}

/** « − » et « + » apparaissent sous le doigt au moment où l'option est choisie : le
 *  second toucher d'un double toucher ne doit pas les actionner. */
const AFTER_SELECT_GRACE_MS = 400;

/** Le plat a changé sous le geste (retiré, table remise à la caisse, soldée…) :
 *  ce n'est pas la peine de réessayer tel quel. */
function isStale(error: unknown): boolean {
  const status = (error as { status?: number } | undefined)?.status;
  return status === 404 || status === 409;
}

/**
 * SC-1739 — ajouter une option sur un plat déjà envoyé, depuis le carnet comme
 * depuis le QR de la table. Rien ne passe par le panier : Envoyer part tout de
 * suite, en tournée à part. Rendue dans `document.body` : le panneau de la table
 * est un tiroir, et elle doit passer au-dessus.
 */
export default function AddOptionsSheet({
  product,
  dishName,
  plateCount,
  carried,
  isBuffetDineIn,
  mode,
  onCancel,
  onSubmit,
}: AddOptionsSheetProps) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const [plates, setPlates] = useState(1);
  const [selection, setSelection] = useState<AddonSelection>({});
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const justSelected = useRef<{ optionId: string; at: number } | null>(null);

  const groups = useMemo(
    () => addableGroups(product, plateCount, carried),
    [product, plateCount, carried],
  );

  // Les places se comptent sur le groupe entier, options retirées de l'écran comprises.
  const fullGroups = product.optionGroups ?? [];
  const fullGroupOf = (group: OptionGroup) => fullGroups.find((g) => g.id === group.id) ?? group;

  useModalBackClose('add-options', onCancel);

  useEffect(() => {
    // Capture : sinon le tiroir de la table, dessous, se ferme avant la fenêtre.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const changePlates = (next: number) => {
    setPlates(next);
    setSelection((current) =>
      clampSelection(fullGroups, current, { plates: next, plateCount, carried }),
    );
    setErrorKey(null);
  };

  const setQty = (optionId: string, qty: number) => {
    setSelection((current) => {
      const { [optionId]: _removed, ...rest } = current;
      return qty > 0 ? { ...rest, [optionId]: qty } : rest;
    });
    setErrorKey(null);
  };

  const stepQty = (optionId: string, qty: number) => {
    const last = justSelected.current;
    if (last?.optionId === optionId && Date.now() - last.at < AFTER_SELECT_GRACE_MS) return;
    setQty(optionId, qty);
  };

  const totalCents = groups.reduce(
    (sum, group) =>
      sum +
      group.options.reduce(
        (groupSum, option) =>
          groupSum +
          optionUnitCents(product, group, option, isBuffetDineIn, mode) *
            (selection[option.id] ?? 0) *
            plates,
        0,
      ),
    0,
  );
  const options = Object.entries(selection).map(([option_value_uuid, qty]) => ({
    option_value_uuid,
    qty,
  }));

  async function handleSend() {
    if (submitting || options.length === 0) return;
    setSubmitting(true);
    setErrorKey(null);
    try {
      await onSubmit({ plates, options });
    } catch (error) {
      setErrorKey(isStale(error) ? 'addOptions.errorChanged' : 'addOptions.errorGeneric');
      setSubmitting(false);
    }
  }

  return createPortal(
    <div
      data-testid="add-options-overlay"
      // Comme la feuille des options : le fond ne ferme pas, un toucher à côté ne
      // doit pas perdre le choix en cours. `waiter-surface` : hors de `.waiter-root`,
      // sans preflight, les bordures valent 0 et les boutons gardent le gris du
      // navigateur. Le fond est écrit en dur : `bg-ink/75` seul reste transparent.
      className="waiter-surface fixed inset-0 z-[1200] flex items-center justify-center p-3"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        data-testid="add-options-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-options-title"
        className="grid grid-rows-[auto_minmax(0,1fr)_auto] grid-cols-[minmax(0,1fr)] w-full max-w-[480px] max-h-[90dvh] rounded-2xl bg-paper text-ink overflow-hidden"
      >
        <div className="flex items-start gap-2 p-3 border-b-2 border-ink">
          <div className="min-w-0 flex-1">
            <h2 id="add-options-title" className="m-0 font-sans text-lg font-bold break-words">
              {t('addOptions.title', { name: dishName })}
            </h2>
            <p className="m-0 mt-1 font-sans text-sm">{t('addOptions.hint')}</p>
          </div>
          <button
            type="button"
            data-fx-mute
            data-testid="add-options-close"
            aria-label={t('common.close')}
            onClick={onCancel}
            className="w-11 h-11 shrink-0 flex items-center justify-center border-2 border-ink rounded-full bg-paper font-sans text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto p-3 space-y-4">
          {plateCount > 1 && (
            <div className="flex items-center justify-between gap-3">
              <span className="font-sans font-bold">{t('addOptions.plates')}</span>
              <span className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  data-fx-mute
                  data-testid="add-options-plates-minus"
                  aria-label={t('addOptions.platesLess')}
                  disabled={plates <= 1}
                  onClick={() => changePlates(plates - 1)}
                  className="w-11 h-11 border-2 border-ink rounded-full bg-paper font-sans text-xl disabled:opacity-30"
                >
                  −
                </button>
                <span
                  data-testid="add-options-plates"
                  className="min-w-[3ch] text-center font-mono font-bold tabular-nums"
                >
                  {t('addOptions.platesValue', { n: plates, total: plateCount })}
                </span>
                <button
                  type="button"
                  data-fx-mute
                  data-testid="add-options-plates-plus"
                  aria-label={t('addOptions.platesMore')}
                  disabled={plates >= plateCount}
                  onClick={() => changePlates(plates + 1)}
                  className="w-11 h-11 border-2 border-ink rounded-full bg-paper font-sans text-xl disabled:opacity-30"
                >
                  +
                </button>
              </span>
            </div>
          )}

          {groups.map((group) => (
            <section key={group.id} data-testid={`add-options-group-${group.id}`}>
              <h3 className="m-0 mb-1.5 font-sans text-base font-bold break-words">
                {ct.name(group)}
              </h3>
              <ul className="list-none m-0 p-0 space-y-1.5">
                {group.options.map((option) => {
                  const qty = selection[option.id] ?? 0;
                  const room = maxPerPlate(fullGroupOf(group), option, {
                    plates,
                    plateCount,
                    carried,
                    selection,
                  });
                  const first = firstQty(option, carried);
                  const unitCents = optionUnitCents(product, group, option, isBuffetDineIn, mode);
                  return (
                    <li
                      key={option.id}
                      data-testid={`add-options-item-${option.id}`}
                      data-selected={qty > 0}
                      className={`flex items-center gap-2 border-2 rounded-lg ${qty > 0 ? 'border-ink bg-cream' : 'border-ink/30'}`}
                    >
                      {/* Le nom sélectionne, il n'ajoute pas : un double toucher ne doit pas
                          facturer deux suppléments. Le « + » est explicite. */}
                      <button
                        type="button"
                        data-fx-mute
                        data-testid={`add-options-tap-${option.id}`}
                        aria-pressed={qty > 0}
                        disabled={qty === 0 && room < first}
                        onClick={() => {
                          if (qty !== 0) return;
                          justSelected.current = { optionId: option.id, at: Date.now() };
                          setQty(option.id, first);
                        }}
                        className="min-w-0 flex-1 min-h-[48px] px-3 py-2 text-start font-sans text-base text-inherit break-words disabled:opacity-40 disabled:cursor-default"
                      >
                        {ct.name(option)}
                        {unitCents > 0 && (
                          <span className="ms-2 font-mono text-sm whitespace-nowrap">
                            <bdi dir="ltr">+{formatPrice(unitCents)}</bdi>
                          </span>
                        )}
                      </button>
                      {qty > 0 && (
                        <span className="flex items-center gap-1 pe-1 shrink-0">
                          <button
                            type="button"
                            data-fx-mute
                            data-testid={`add-options-minus-${option.id}`}
                            aria-label={t('addOptions.optionLess', { name: ct.name(option) })}
                            onClick={() => stepQty(option.id, qty - 1 < first ? 0 : qty - 1)}
                            className="w-11 h-11 border-2 border-ink rounded-full bg-paper font-sans text-xl"
                          >
                            −
                          </button>
                          <span
                            data-testid={`add-options-qty-${option.id}`}
                            className="min-w-[2ch] text-center font-mono font-bold tabular-nums"
                          >
                            {qty}
                          </span>
                          <button
                            type="button"
                            data-fx-mute
                            data-testid={`add-options-plus-${option.id}`}
                            aria-label={t('addOptions.optionMore', { name: ct.name(option) })}
                            disabled={qty >= room}
                            onClick={() => stepQty(option.id, qty + 1)}
                            className="w-11 h-11 border-2 border-ink rounded-full bg-paper font-sans text-xl disabled:opacity-30"
                          >
                            +
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        <div className="flex flex-col gap-2 p-3 border-t-2 border-ink">
          {errorKey && (
            <p
              data-testid="add-options-error"
              role="alert"
              className="m-0 font-sans text-sm font-bold"
            >
              {t(errorKey)}
            </p>
          )}
          {/* La place est réservée : sinon Envoyer et l'option sous le doigt bougent au
              premier choix. */}
          <div className="min-h-6">
            {totalCents > 0 && (
              <p data-testid="add-options-total" className="m-0 font-sans text-base font-bold">
                {t('addOptions.extra', { amount: formatPrice(totalCents) })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-fx-mute
              data-testid="add-options-cancel"
              onClick={onCancel}
              className="h-12 flex-1 border-2 border-ink rounded-btn bg-paper font-sans text-base font-bold"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              data-fx-mute
              data-testid="add-options-send"
              disabled={options.length === 0 || submitting}
              onClick={() => void handleSend()}
              className="h-12 flex-1 rounded-btn font-sans text-base font-bold text-paper disabled:opacity-40"
              style={{ backgroundColor: 'var(--ink)' }}
            >
              {t('addOptions.send')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
